import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import type { JSX, ReactNode } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { MedicationElationDraftActions } from '#src/components/medications/MedicationElationDraftActions.tsx'
import { MedicationHistoryTooltip } from '#src/components/medications/MedicationHistoryTooltip.tsx'
import { getMedicationThreadId } from '#src/hooks/useMedicationDraftsByThread.ts'

export function MedicationRefillRow({
  display,
  href,
  medication,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
}: {
  display: string
  href?: string
  medication?: ExtendedMedicationRequest
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: 'Refill'
  ) => void | Promise<number | undefined>
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
}): JSX.Element {
  const link: ReactNode = href ? (
    <TextLink to={href}>{display}</TextLink>
  ) : (
    <span>{display}</span>
  )

  if (!medication) {
    return <div className="flex items-center gap-2 py-1 text-sm">{link}</div>
  }

  const threadId = getMedicationThreadId(medication)
  const draftId = threadId ? draftIdsByThreadId?.get(threadId) : undefined

  return (
    <div className="flex items-center gap-2 py-1 text-sm">
      <HoverCard openDelay={300} closeDelay={200}>
        <HoverCardTrigger asChild>
          <span>{link}</span>
        </HoverCardTrigger>
        <HoverCardContent side="bottom" align="start" className="w-auto p-0">
          <MedicationHistoryTooltip
            medicationDisplay={medication.medicationDisplay ?? display}
            medicationRequests={[medication]}
            onDraftPrescription={onDraftPrescription}
            onOpenDraftPrescription={onOpenDraftPrescription}
            draftId={draftId}
            isCreatingDraft={draftingMedicationIds?.has(medication.id)}
          />
        </HoverCardContent>
      </HoverCard>
      <MedicationElationDraftActions
        medication={medication}
        onDraftPrescription={onDraftPrescription}
        onOpenDraftPrescription={onOpenDraftPrescription}
        draftIdsByThreadId={draftIdsByThreadId}
        draftingMedicationIds={draftingMedicationIds}
      />
    </div>
  )
}
